/**
 * Versão do motor de renderização, extraída do user agent (Renato 2026-09-20).
 *
 * Por que isso importa e não é curiosidade: a versão do Chromium decide três
 * comportamentos do IndexedDB que mudam como o player se comporta em campo, e
 * hoje a frota é uma caixa-preta nesse ponto.
 *
 *  - **≥ 156** — um store NOVO nasce em SQLite em vez de LevelDB
 *    (chromestatus 5161589557821440, ainda "Proposed" em 20/09/2026). Banco já
 *    existente NÃO migra: "existing data stored in LevelDB is unimpacted".
 *    Só store novo — primeira visita à origem ou após limpar os dados do site.
 *  - **≥ 121** — `durability: 'relaxed'` virou o PADRÃO. Abaixo disso o padrão
 *    é estrito e vale pedir relaxed explicitamente.
 *  - **≥ 83** — a opção `durability` existe. Abaixo, é ignorada.
 *
 * No Android o WebView atualiza pela Play Store, por device, então a frota é
 * necessariamente MISTA. Sem este campo não há como saber qual tablet está em
 * qual regime — nem para diagnosticar, nem para decidir uma migração.
 *
 * Vai em `device_info.extra`, que o servidor já aceita como campo livre —
 * sem migration, sem mudança de DTO.
 */

export interface EngineInfo {
  /** Motor detectado. Tizen e webOS modernos também reportam Chrome/NN. */
  engine: 'chromium' | 'webkit' | 'unknown';
  /** Major do Chromium (ex.: 153). Null quando não dá pra determinar. */
  engineVersion: number | null;
  /** Versão completa, quando disponível (ex.: "153.0.8010.37"). */
  engineVersionFull: string | null;
}

export function detectEngineVersion(
  userAgent: string = typeof navigator !== 'undefined'
    ? navigator.userAgent
    : '',
): EngineInfo {
  const chromium = userAgent.match(/Chrome\/(\d+)(?:\.[\d.]+)?/);
  if (chromium) {
    const full = userAgent.match(/Chrome\/([\d.]+)/)?.[1] ?? null;
    return {
      engine: 'chromium',
      engineVersion: Number(chromium[1]),
      engineVersionFull: full,
    };
  }

  // WebKit puro (Tizen antigo, Safari). Não tem major de Chromium.
  const webkit = userAgent.match(/AppleWebKit\/([\d.]+)/);
  if (webkit) {
    return {
      engine: 'webkit',
      engineVersion: null,
      engineVersionFull: webkit[1],
    };
  }

  return { engine: 'unknown', engineVersion: null, engineVersionFull: null };
}
